import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import {
  Brain, Download, Loader2, Plus, Trash2, X,
} from "lucide-react";

/**
 * MemoryCard — D6a (iter134).
 *
 * Typed, user-editable memory. Reads /coach/memory (auto-facts are
 * synced compute-on-read from Day Zero + Session Contract +
 * aggregates). No LLM anywhere.
 *
 * User can:
 *  - View grouped by kind (rule / milestone / context / aha / preference)
 *  - Add aha / preference facts
 *  - Edit editable facts (only aha / preference for now)
 *  - Soft-delete any fact (DPDP erasure trail)
 *  - Export the full JSON (DPDP portability)
 */
export default function MemoryCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedKind, setExpandedKind] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/memory");
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const total = data?.total || 0;

  return (
    <section
      data-testid="memory-card-section"
      className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-4"
    >
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Brain className="w-4 h-4 text-[var(--bt-accent)]" />
          <div>
            <p className="bt-label">Coach Memory · DPDP</p>
            <h2 className="font-heading text-xl">
              What Coach Knows About You
              {total > 0 && (
                <span className="text-sm text-[var(--bt-text-muted)] font-sans ml-2">
                  · {total} {total === 1 ? "fact" : "facts"}
                </span>
              )}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="memory-add-btn"
            onClick={() => setShowAdd((v) => !v)}
            variant="outline"
            className="h-8 rounded-sm border-[var(--bt-border)] text-xs font-mono uppercase"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add note
          </Button>
          <Button
            data-testid="memory-export-btn"
            onClick={async () => {
              const { data } = await api.get("/coach/memory/export");
              const blob = new Blob([JSON.stringify(data, null, 2)],
                                    { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `braintrades-memory-${new Date().toISOString().slice(0,10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            variant="outline"
            className="h-8 rounded-sm border-[var(--bt-border)] text-xs font-mono uppercase"
          >
            <Download className="w-3 h-3 mr-1" />
            Export JSON
          </Button>
        </div>
      </header>

      <p className="text-xs text-[var(--bt-text-muted)] leading-relaxed">
        Every fact below is derived from something <em>you</em> gave us — your
        Day Zero answers, Session Contract, or your own trade history.
        Nothing here is generated by an LLM. You can add, edit, or erase
        any of it. Erased facts are kept as a soft-deleted audit trail
        per India's DPDP Act.
      </p>

      {showAdd && (
        <AddFactForm
          onDone={() => { setShowAdd(false); refresh(); }}
        />
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--bt-text-muted)] py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : !data || total === 0 ? (
        <div
          data-testid="memory-empty"
          className="text-sm text-[var(--bt-text-muted)] py-8 text-center border border-dashed border-[var(--bt-border)] rounded-sm"
        >
          No facts yet — complete your Day Zero setup or capture a
          thesis and Coach will start remembering.
        </div>
      ) : (
        <div className="space-y-3">
          <div
            className="flex items-center gap-2 flex-wrap"
            data-testid="memory-kind-pills"
          >
            {[
              ["rule",        "Rules"],
              ["milestone",   "Milestones"],
              ["context",     "Context"],
              ["aha",         "Aha"],
              ["preference",  "Notes"],
            ].map(([k, label]) => {
              const count = data.counts[k] || 0;
              if (count === 0) return null;
              const active = expandedKind === k;
              return (
                <button
                  key={k}
                  data-testid={`memory-kind-pill-${k}`}
                  onClick={() => setExpandedKind(active ? null : k)}
                  className={`px-3 py-1 text-xs font-mono uppercase rounded-sm border transition-colors ${
                    active
                      ? "border-[var(--bt-accent)] text-[var(--bt-accent)]"
                      : "border-[var(--bt-border)] text-[var(--bt-text-muted)] hover:text-[var(--bt-text)]"
                  }`}
                >
                  {label} · {count}
                </button>
              );
            })}
          </div>
          {expandedKind && (
            <FactList
              kind={expandedKind}
              facts={data.facts[expandedKind] || []}
              onChanged={refresh}
            />
          )}
        </div>
      )}
    </section>
  );
}

function FactList({ kind, facts, onChanged }) {
  if (facts.length === 0) {
    return (
      <div className="text-sm text-[var(--bt-text-muted)] py-4">
        Nothing in this bucket yet.
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid={`memory-fact-list-${kind}`}>
      {facts.map((f) => (
        <FactRow key={f.id} fact={f} onChanged={onChanged} />
      ))}
    </ul>
  );
}

function FactRow({ fact, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(fact.value ?? ""));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/coach/memory/${encodeURIComponent(fact.id)}`,
                       { value: draft });
      setEditing(false);
      onChanged();
    } catch {
      /* silent */
    } finally {
      setBusy(false);
    }
  };

  const erase = async () => {
    if (!window.confirm("Erase this fact? A soft-deleted audit trail is kept per DPDP.")) return;
    setBusy(true);
    try {
      await api.delete(`/coach/memory/${encodeURIComponent(fact.id)}`);
      onChanged();
    } catch {
      /* silent */
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      data-testid={`memory-fact-row-${fact.kind}-${fact.key}`}
      className="border border-[var(--bt-border)] rounded-sm p-3 flex items-start justify-between gap-3 flex-wrap"
    >
      <div className="min-w-[220px] flex-1">
        <p className="text-xs font-mono text-[var(--bt-text-muted)] uppercase">
          {fact.key}
          <span className="ml-2 lowercase text-[10px]">
            · {fact.source}
          </span>
        </p>
        {editing ? (
          <textarea
            data-testid={`memory-fact-edit-input-${fact.kind}-${fact.key}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full mt-1 px-2 py-1 text-sm bg-[var(--bt-bg)] border border-[var(--bt-border)] rounded-sm font-sans"
            rows={2}
            maxLength={500}
          />
        ) : (
          <p className="text-sm font-sans mt-1 break-words">
            {typeof fact.value === "object"
              ? JSON.stringify(fact.value)
              : String(fact.value)}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {editing ? (
          <>
            <Button
              data-testid={`memory-fact-save-${fact.kind}-${fact.key}`}
              onClick={save}
              disabled={busy}
              className="h-7 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black text-xs font-mono uppercase"
            >
              Save
            </Button>
            <button
              data-testid={`memory-fact-cancel-${fact.kind}-${fact.key}`}
              onClick={() => { setEditing(false); setDraft(String(fact.value ?? "")); }}
              className="text-[var(--bt-text-muted)] hover:text-[var(--bt-text)]"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            {fact.editable && (
              <button
                data-testid={`memory-fact-edit-${fact.kind}-${fact.key}`}
                onClick={() => setEditing(true)}
                className="text-xs font-mono uppercase text-[var(--bt-text-muted)] hover:text-[var(--bt-accent)]"
              >
                Edit
              </button>
            )}
            <button
              data-testid={`memory-fact-erase-${fact.kind}-${fact.key}`}
              onClick={erase}
              disabled={busy}
              className="text-[var(--bt-text-muted)] hover:text-[var(--bt-loss)]"
              title="Erase this fact (DPDP)"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function AddFactForm({ onDone }) {
  const [kind, setKind] = useState("aha");
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    if (!key.trim() || !value.trim()) {
      setErr("Both key and value are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/coach/memory", {
        kind, key: key.trim(), value: value.trim(),
      });
      setKey(""); setValue(""); onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="memory-add-form"
      className="border border-dashed border-[var(--bt-accent)] rounded-sm p-4 space-y-3"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs font-mono uppercase text-[var(--bt-text-muted)]">
          Kind
        </label>
        {["aha", "preference"].map((k) => (
          <button
            key={k}
            data-testid={`memory-add-kind-${k}`}
            onClick={() => setKind(k)}
            className={`px-3 py-1 text-xs font-mono uppercase rounded-sm border transition-colors ${
              kind === k
                ? "border-[var(--bt-accent)] text-[var(--bt-accent)]"
                : "border-[var(--bt-border)] text-[var(--bt-text-muted)]"
            }`}
          >
            {k}
          </button>
        ))}
      </div>
      <input
        data-testid="memory-add-key-input"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="e.g. avoid_symbol"
        maxLength={80}
        className="w-full px-2 py-1 text-sm bg-[var(--bt-bg)] border border-[var(--bt-border)] rounded-sm font-mono"
      />
      <textarea
        data-testid="memory-add-value-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="What should Coach remember?"
        maxLength={500}
        rows={2}
        className="w-full px-2 py-1 text-sm bg-[var(--bt-bg)] border border-[var(--bt-border)] rounded-sm font-sans"
      />
      {err && (
        <p
          data-testid="memory-add-error"
          className="text-xs text-[var(--bt-loss)]"
        >
          {err}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          data-testid="memory-add-cancel"
          onClick={() => onDone()}
          variant="outline"
          className="h-8 rounded-sm border-[var(--bt-border)] text-xs font-mono uppercase"
        >
          Cancel
        </Button>
        <Button
          data-testid="memory-add-submit"
          onClick={submit}
          disabled={busy}
          className="h-8 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black text-xs font-mono uppercase"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
